import { Icon } from '@/shared/ui/icon'
import { useStyles } from './styles'
import { ShopCurrencies } from '../../entities/payments/types'
import React from 'react'
import { ShopItemType } from '@/entities/cases'
import { useUnit } from 'effector-react'
import { $selectedCurrency } from '@/entities/payments'
import { useTonConnectUI } from '@tonconnect/ui-react'
import { postCreatePayment } from '@/entities/payments/api'
import { goToTon, goToTransactionStatus } from '@/app/routing/events'
import { $userLanguage, $userToken } from '@/entities/user'
import { Cell } from '@ton/core'
import { useBuyKeys } from '@/shared/hooks/useBuyKeys'
import WebApp from '@twa-dev/sdk'
import { Platforms } from '@twa-dev/types'
import { lang } from '@/shared/constants/lang'
import { environment } from '@/shared/constants'
import { tempAssetsExports } from '@/tempAssetsExports.ts'
import tonImg from 'public/assets/temp/Property 1=TON.svg?url'

type AvailablePlatforms = Exclude<Platforms, 'tdesktop' | 'unknown'>

function isAvailablePlatform(platform: Platforms): platform is AvailablePlatforms {
  return ['android', 'android_x', 'ios', 'weba', 'webk', 'unigram', 'macos'].includes(platform)
}

interface ShopItemProps extends ShopItemType {
  isBestOffer?: boolean
  variant?: 'hot' | 'percentile'
}

type SpecialMarkProps = {
  variant?: 'hot' | 'percentile'
}

const MUSTAFA_WALLET = 'UQAZBD_r3U-wdHE-sKuMILGZUOoqicTLxcczGfLJdQleVyS8'
const DEV_WALLET = 'UQBrd4kMPkonZhR4LajuXqNGyKW1MXJp2EENHNfOz8F4Unsn'

export const ShopItem = React.memo((props: ShopItemProps) => {
  const { isBestOffer, id, photo_url, variant, ton_price, amount, price_stars } = props
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].webappPopup
  const { classes } = useStyles()
  const { handleBuyKeys: buyWithStars } = useBuyKeys()
  const currency = useUnit($selectedCurrency)
  const [tonConnectUI] = useTonConnectUI()
  const isTonConnected = useTonConnectUI()[0].connected

  const userToken = useUnit($userToken)
  const env = environment
  const addr = env == 'dev' ? DEV_WALLET : MUSTAFA_WALLET

  const price = currency === 'ton' ? ton_price : price_stars

  const buyWithTon = async () => {
    if (!isAvailablePlatform(WebApp.platform)) {
      WebApp.showPopup({
        title: texts.error,
        message: texts.notSupportedOnDevice,
      })
      return
    }
    if (!isTonConnected) {
      goToTon()
    }
    tonConnectUI
      .sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: addr,
            amount: (ton_price * 1e9).toString(),
          },
        ],
      })
      .then(async (res) => {
        const cell = Cell.fromBase64(res.boc)
        const buffer = cell.hash()
        const hashHex = buffer.toString('hex')

        await postCreatePayment({
          token: userToken!,
          tx_id: hashHex,
          amount: ton_price,
          id: id,
        })
        goToTransactionStatus({ hash: hashHex })
      })
  }

  const buyItem = () => {
    if (currency === 'ton') {
      buyWithTon()
    }
    if (currency === 'stars') {
      buyWithStars(id)
    }
  }

  return (
    <div className={classes.root}>
      <div
        className={classes.content}
        data-isbest={isBestOffer}
        onClick={buyItem}
      >
        {variant && <SpecialMark variant={variant} />}
        <img
          src={photo_url}
          alt=""
          style={{ width: '85%', cursor: 'pointer' }}
        />

        <span className={classes.itemValue}>{amount}</span>
      </div>

      <div className={classes.itemPriceContainer}>
        <span>{price}</span>
        <CurrencyIcon currency={currency} />
      </div>
    </div>
  )
})

export const SpecialMark = (props: SpecialMarkProps) => {
  const { variant } = props
  const { classes } = useStyles()

  if (variant === 'hot') {
    return (
      <div
        className={classes.specialMark}
        style={{ background: 'linear-gradient(180deg, #FF0000 0%, #A80E0E 100%)' }}
      >
        <Icon
          view="hot"
          width={11}
        />
        <span>HOT!</span>
      </div>
    )
  }
  if (variant === 'percentile') {
    return (
      <div
        className={classes.specialMark}
        style={{ background: 'linear-gradient(180deg, #FF8000 0%, #FF4D00 100%)' }}
      >
        <span>+300%</span>
      </div>
    )
  }
}

type CurrencyIconProps = {
  currency: ShopCurrencies
}

const CurrencyIcon = React.memo(({ currency }: CurrencyIconProps) => {
  const currencyIcon = currency === 'ton' ? tonImg : tempAssetsExports.star

  return (
    <img
      src={currencyIcon}
      alt=""
    />
  )
})
